<template>
  <BaseCard type="filled" class="relative flex w-full flex-col p-4">
    <div class="flex flex-row">
      <div class="flex min-w-0 grow flex-col">
        <p class="text-body-medium text-on-surface-variant">Feedback-Vorgabe</p>
        <h3 class="text-title-large truncate">{{ feedbackGoal.feedbackTopic.title }}</h3>
      </div>
      <BaseTextButton
        size="XS"
        width="wide"
        color="tonalPrimary"
        @click="startSurvey"
        class="-mr-1"
      >
        {{ $t('common.requestFeedback') }}
      </BaseTextButton>
    </div>
    <div class="mt-2 flex w-full flex-row gap-0.5">
      <div
        v-for="perspective in perspectives"
        :key="perspective.perspectiveKey"
        class="flex grow basis-0 flex-row items-center"
      >
        <BaseProgressRing
          :radius="20"
          :thickness="16"
          :percentage="perspective.percentage"
          :icon="iconFor(perspective.perspectiveKey)"
          :color="perspective.color"
        />
        <div class="ml-2 flex flex-col">
          <span class="text-title-medium font-medium"
            >{{ perspective.done }}/{{ perspective.goal }}</span
          >
          <span class="text-body-small text-on-surface-variant">{{
            labelFor(perspective.perspectiveKey)
          }}</span>
        </div>
      </div>
    </div>
  </BaseCard>
</template>
<script lang="ts" setup>
  import type { FeedbackGoal } from '@/modules/task/composables/useFeedbackGoals'
  import BaseCard from '../../../core/components/base/card/BaseCard.vue'
  import BaseProgressRing from '../../../core/components/base/progress-ring/BaseProgressRing.vue'
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { iconForPerspective } from '../../../core/models/feedback-perspective'
  import { findPerspective, resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { useFeedbackGoalProgress } from '@/modules/coachees/composables/use-coachee-feedback-goal-progress'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useFeedbackTopicStore } from '@/stores/feedback-topic.store.ts'

  interface Props {
    feedbackGoal: FeedbackGoal
  }

  const { feedbackGoal } = defineProps<Props>()
  const { perspectives } = useFeedbackGoalProgress(() => feedbackGoal.progress)
  const { t } = useI18n()
  const { templates } = storeToRefs(useFeedbackTopicStore())

  const topicPerspectives = computed(
    () =>
      templates.value.find((template) => template.id === feedbackGoal.feedbackTopic.id)
        ?.perspectives,
  )

  const iconFor = (key: string) =>
    iconForPerspective(findPerspective(key, topicPerspectives.value) ?? key)

  const labelFor = (key: string) =>
    resolvePerspectiveLabel(key, topicPerspectives.value, {
      selfLabel: t('perspectives.self.default'),
    })

  const router = useRouter()
  const startSurvey = () => {
    router.push({ query: { requestDialog: '1', templateId: feedbackGoal.feedbackTopic.id } })
  }
</script>
